import { useEffect, useMemo, useRef } from 'react';
import { layoutFolderTree } from '../../DocsArea/Atlas/layoutTree';
import { buildCampaignAtlasItems } from '../buildCampaignAtlasItems';

/**
 * Campaign tree + peek payload for Campaigns Atlas.
 */
export function useCampaignsAtlasTree({
  sprints,
  sprintOrder,
  expandedId,
  fitToBounds,
  nodeHeights = null,
}) {
  const hasFittedRef = useRef(false);

  const items = useMemo(
    () => buildCampaignAtlasItems(sprints, sprintOrder),
    [sprints, sprintOrder]
  );

  const { nodes, edges, bounds, clusters } = useMemo(
    () => layoutFolderTree(items, { heights: nodeHeights }),
    [items, nodeHeights]
  );

  const laidOut = useMemo(() => {
    const byId = new Map(items.map((item) => [item.id, item]));
    return nodes.map((n) => {
      const item = byId.get(n.id);
      return {
        ...n,
        icon: item?.icon || null,
        sprint: item?.sprint || null,
        childCount: n.docCount || 0,
      };
    });
  }, [nodes, items]);

  useEffect(() => {
    if (laidOut.length === 0) {
      hasFittedRef.current = false;
      return;
    }
    if (hasFittedRef.current) return;
    hasFittedRef.current = true;
    fitToBounds(bounds, 48);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [laidOut.length]);

  const peek = useMemo(() => {
    if (!expandedId) return null;
    const children = items.filter((item) => item.parentId === expandedId);
    return {
      folderId: expandedId,
      groups: children.length
        ? [{
            key: 'nested',
            label: 'Nested',
            shown: children.map((c) => ({
              id: c.id,
              title: c.name,
              icon: c.icon,
              sprint: c.sprint,
            })),
            more: 0,
          }]
        : [],
      empty: children.length === 0,
    };
  }, [expandedId, items]);

  return { nodes: laidOut, edges, bounds, clusters, peek, treeReady: true };
}
